import React, { FormEvent, startTransition, useDeferredValue, useEffect, useState } from 'react';
import {
  BarChart3,
  BellRing,
  ChevronDown,
  CircleAlert,
  ClipboardList,
  Loader2,
  LogIn,
  LogOut,
  Search,
  Send,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';
import { AnalyticsPanel } from './analytics/AnalyticsPanel';
import { ApiError, createDispatch, getAdminSession, getDispatch, listRegisteredDevices } from './lib/api';
import { getAccessToken, initializeAuth, signIn, signOut, type AdminAccount } from './lib/auth';
import { configIssues, isEntraConfigured, adminConfig } from './lib/config';
import type {
  MetadataValue,
  NotificationAudience,
  NotificationAudienceKind,
  NotificationDispatchRequest,
  NotificationDispatchResponse,
  RegisteredPushDevice,
} from './types';

type DispatchForm = {
  intent: string;
  title: string;
  body: string;
  route: string;
  symbol: string;
  dataJson: string;
  metadataJson: string;
  audienceKind: NotificationAudienceKind;
  uids: string;
  platform: string;
  priority: string;
  sound: string;
  ttlSeconds: string;
  dryRun: boolean;
};

type AdminSection = 'analytics' | 'dispatch' | 'tokens';

const initialForm: DispatchForm = {
  intent: 'announcement',
  title: '',
  body: '',
  route: '',
  symbol: '',
  dataJson: '{\n  "source": "admin"\n}',
  metadataJson: '{}',
  audienceKind: 'test_devices',
  uids: '',
  platform: 'ios',
  priority: 'normal',
  sound: 'default',
  ttlSeconds: '',
  dryRun: true,
};

export function App() {
  const [user, setUser] = useState<AdminAccount | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState('');
  const [unauthorizedAccount, setUnauthorizedAccount] = useState<AdminAccount | null>(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<AdminSection>('analytics');
  const [form, setForm] = useState(initialForm);
  const [dispatchId, setDispatchId] = useState('');
  const [result, setResult] = useState<NotificationDispatchResponse | null>(null);
  const [devices, setDevices] = useState<RegisteredPushDevice[]>([]);
  const [operationError, setOperationError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingDevices, setIsFetchingDevices] = useState(false);
  const deferredDeliveries = useDeferredValue(result?.deliveries ?? []);

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      let account: AdminAccount | null = null;
      try {
        account = await initializeAuth();
        if (!account) {
          return;
        }
        await getAdminSession(await getAccessToken());
        if (!cancelled) {
          setUser(account);
        }
      } catch (error) {
        if (!cancelled) {
          setAuthError(describeAuthError(error));
          setUnauthorizedAccount(error instanceof ApiError && error.status === 403 ? account : null);
        }
      } finally {
        if (!cancelled) {
          setAuthReady(true);
        }
      }
    }

    void restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSignIn() {
    setAuthError('');
    try {
      await signIn();
    } catch (error) {
      setAuthError(describeAuthError(error));
    }
  }

  async function handleSignOut() {
    setResult(null);
    setDispatchId('');
    setDevices([]);
    setOperationError('');
    setAccountMenuOpen(false);
    await signOut();
  }

  async function loadDevices(currentUser = user) {
    if (!currentUser) {
      setOperationError('Sign in before loading registered devices.');
      return;
    }

    setIsFetchingDevices(true);
    setOperationError('');
    try {
      const token = await getAccessToken();
      const response = await listRegisteredDevices(token);
      startTransition(() => setDevices(response.devices));
    } catch (error) {
      setOperationError(error instanceof Error ? error.message : 'Device lookup failed.');
    } finally {
      setIsFetchingDevices(false);
    }
  }

  async function handleCreateDispatch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user) {
      setOperationError('Sign in before sending a dispatch.');
      return;
    }

    setIsSubmitting(true);
    setOperationError('');
    try {
      const token = await getAccessToken();
      const payload = buildDispatchPayload(form);
      const response = await createDispatch(token, payload);
      startTransition(() => {
        setResult(response);
        setDispatchId(String(response.dispatch_id));
      });
    } catch (error) {
      setOperationError(error instanceof Error ? error.message : 'Dispatch failed.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleFetchDispatch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user) {
      setOperationError('Sign in before loading dispatch history.');
      return;
    }

    const parsedDispatchId = Number(dispatchId);
    if (!Number.isInteger(parsedDispatchId) || parsedDispatchId < 1) {
      setOperationError('Enter a valid dispatch ID.');
      return;
    }

    setIsFetching(true);
    setOperationError('');
    try {
      const token = await getAccessToken();
      const response = await getDispatch(token, parsedDispatchId);
      startTransition(() => setResult(response));
    } catch (error) {
      setOperationError(error instanceof Error ? error.message : 'Lookup failed.');
    } finally {
      setIsFetching(false);
    }
  }

  const authLabel = user?.username || user?.name || '';

  return (
    <main className={`app-shell ${user ? '' : 'app-shell-login'}`}>
      <header className="topbar">
        <div>
          <p className="eyebrow">Ascensa Operations</p>
          <h1>Admin Console</h1>
        </div>
        {user ? (
          <div className="account-menu">
            <button
              type="button"
              className="account-trigger"
              aria-haspopup="menu"
              aria-expanded={accountMenuOpen}
              title={authLabel}
              onClick={() => setAccountMenuOpen((open) => !open)}
            >
              <ShieldCheck size={18} aria-hidden="true" />
              <span>{authLabel}</span>
              <ChevronDown size={16} aria-hidden="true" />
            </button>
            {accountMenuOpen ? (
              <div className="account-popover" role="menu">
                <button type="button" role="menuitem" onClick={handleSignOut}>
                  <LogOut size={17} aria-hidden="true" />
                  <span>Sign out</span>
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </header>

      {configIssues.length > 0 ? (
        <Notice tone="warning" title="Configuration needed" items={configIssues} />
      ) : null}

      {!user ? (
        <section className="login-grid">
          <AuthPanel
            authReady={authReady}
            authError={authError}
            configured={isEntraConfigured}
            unauthorizedAccount={unauthorizedAccount}
            onSignIn={handleSignIn}
            onSignOut={handleSignOut}
          />
        </section>
      ) : (
      <section className="dashboard-grid">
        <aside className="nav-blade" aria-label="Admin sections">
          <nav className="section-nav">
            <button
              type="button"
              className={`nav-item ${activeSection === 'analytics' ? 'nav-item-active' : ''}`}
              aria-current={activeSection === 'analytics' ? 'page' : undefined}
              onClick={() => setActiveSection('analytics')}
            >
              <BarChart3 size={18} aria-hidden="true" />
              <span>Analytics</span>
            </button>
            <button
              type="button"
              className={`nav-item ${activeSection === 'dispatch' ? 'nav-item-active' : ''}`}
              aria-current={activeSection === 'dispatch' ? 'page' : undefined}
              onClick={() => setActiveSection('dispatch')}
            >
              <BellRing size={18} aria-hidden="true" />
              <span>Dispatch</span>
            </button>
            <button
              type="button"
              className={`nav-item ${activeSection === 'tokens' ? 'nav-item-active' : ''}`}
              aria-current={activeSection === 'tokens' ? 'page' : undefined}
              onClick={() => setActiveSection('tokens')}
            >
              <Smartphone size={18} aria-hidden="true" />
              <span>Token Registration</span>
            </button>
          </nav>
        </aside>

        <section className="dashboard-content">
        {activeSection === 'analytics' ? (
          <AnalyticsPanel account={user} />
        ) : activeSection === 'dispatch' ? (
        <div className="dispatch-layout">
        <section className="panel api-panel">
          <div className="panel-heading">
            <BellRing size={20} aria-hidden="true" />
            <div>
              <h2>Dispatch</h2>
              <p>{adminConfig.apiBaseUrl}</p>
            </div>
          </div>

          <form className="dispatch-form" onSubmit={handleCreateDispatch}>
            <div className="field-grid two-columns">
              <Field label="Intent">
                <input value={form.intent} onChange={(event) => updateForm('intent', event.target.value, setForm)} required maxLength={32} />
              </Field>
              <Field label="Audience">
                <select
                  value={form.audienceKind}
                  onChange={(event) => updateForm('audienceKind', event.target.value as NotificationAudienceKind, setForm)}
                >
                  <option value="test_devices">Test devices</option>
                  <option value="uids">UIDs</option>
                  <option value="platform">Platform</option>
                  <option value="all_users">All enabled devices</option>
                </select>
              </Field>
            </div>

            <Field label="Title">
              <input value={form.title} onChange={(event) => updateForm('title', event.target.value, setForm)} required maxLength={128} />
            </Field>
            <Field label="Body">
              <textarea value={form.body} onChange={(event) => updateForm('body', event.target.value, setForm)} required maxLength={512} rows={4} />
            </Field>

            <div className="field-grid two-columns">
              <Field label="Route">
                <input value={form.route} onChange={(event) => updateForm('route', event.target.value, setForm)} placeholder="/ticker/AAPL" maxLength={256} />
              </Field>
              <Field label="Symbol">
                <input value={form.symbol} onChange={(event) => updateForm('symbol', event.target.value.toUpperCase(), setForm)} placeholder="AAPL" maxLength={16} />
              </Field>
            </div>

            <div className="field-grid two-columns">
              <Field label="UIDs">
                <textarea value={form.uids} onChange={(event) => updateForm('uids', event.target.value, setForm)} placeholder="uid-one, uid-two" rows={3} />
              </Field>
              <Field label="Platform">
                <select value={form.platform} onChange={(event) => updateForm('platform', event.target.value, setForm)}>
                  <option value="ios">iOS</option>
                  <option value="android">Android</option>
                </select>
              </Field>
            </div>

            <div className="field-grid three-columns">
              <Field label="Priority">
                <select value={form.priority} onChange={(event) => updateForm('priority', event.target.value, setForm)}>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </select>
              </Field>
              <Field label="Sound">
                <input value={form.sound} onChange={(event) => updateForm('sound', event.target.value, setForm)} placeholder="default" maxLength={32} />
              </Field>
              <Field label="TTL seconds">
                <input value={form.ttlSeconds} onChange={(event) => updateForm('ttlSeconds', event.target.value, setForm)} inputMode="numeric" placeholder="3600" />
              </Field>
            </div>

            <div className="field-grid two-columns">
              <Field label="Data JSON">
                <textarea className="mono" value={form.dataJson} onChange={(event) => updateForm('dataJson', event.target.value, setForm)} rows={5} />
              </Field>
              <Field label="Metadata JSON">
                <textarea className="mono" value={form.metadataJson} onChange={(event) => updateForm('metadataJson', event.target.value, setForm)} rows={5} />
              </Field>
            </div>

            <label className="toggle-row">
              <input type="checkbox" checked={form.dryRun} onChange={(event) => updateForm('dryRun', event.target.checked, setForm)} />
              <span>Dry run</span>
            </label>

            <button className="primary-action" type="submit" disabled={!user || isSubmitting}>
              {isSubmitting ? <Loader2 className="spin" size={18} aria-hidden="true" /> : <Send size={18} aria-hidden="true" />}
              <span>{form.dryRun ? 'Create dry run' : 'Send dispatch'}</span>
            </button>
          </form>
        </section>

        <section className="panel results-panel">
          <div className="panel-heading">
            <ClipboardList size={20} aria-hidden="true" />
            <div>
              <h2>Result</h2>
              <p>{result ? `Dispatch ${result.dispatch_id}` : 'No dispatch loaded'}</p>
            </div>
          </div>

          <form className="lookup-row" onSubmit={handleFetchDispatch}>
            <input value={dispatchId} onChange={(event) => setDispatchId(event.target.value)} inputMode="numeric" placeholder="Dispatch ID" />
            <button type="submit" disabled={!user || isFetching}>
              {isFetching ? <Loader2 className="spin" size={17} aria-hidden="true" /> : <Search size={17} aria-hidden="true" />}
              <span>Load</span>
            </button>
          </form>

          {operationError ? <Notice tone="error" title="Request failed" items={[operationError]} /> : null}

          {result ? (
            <>
              {result.target_count === 0 ? (
                <Notice
                  tone="warning"
                  title="No matching devices"
                  items={['No enabled push tokens matched this audience. Open the mobile app and use Register + test push from Account, then send again.']}
                />
              ) : null}
              <div className="stat-grid">
                <Stat label="Status" value={result.status} />
                <Stat label="Targets" value={result.target_count} />
                <Stat label="Attempted" value={result.attempted_count} />
                <Stat label="Sent" value={result.sent_count} />
                <Stat label="Errors" value={result.error_count} />
              </div>
              <div className="delivery-list">
                {deferredDeliveries.length === 0 ? (
                  <p className="empty-state">No delivery rows returned.</p>
                ) : (
                  deferredDeliveries.map((delivery) => (
                    <article className="delivery-card" key={`${delivery.uid}-${delivery.token_prefix}`}>
                      <div>
                        <strong>{delivery.uid}</strong>
                        <span>{delivery.platform} · {delivery.token_prefix}</span>
                      </div>
                      <div className={`status-badge status-${delivery.status}`}>{delivery.status}</div>
                      {delivery.error ? <p>{delivery.error}</p> : null}
                    </article>
                  ))
                )}
              </div>
            </>
          ) : (
            <p className="empty-state">Create or load a dispatch to see delivery details.</p>
          )}
        </section>
        </div>
        ) : activeSection === 'tokens' ? (
          <DevicesPanel
            devices={devices}
            error={operationError}
            isFetching={isFetchingDevices}
            onRefresh={() => void loadDevices()}
          />
        ) : null}
        </section>
      </section>
      )}
    </main>
  );
}

function AuthPanel(props: {
  authReady: boolean;
  authError: string;
  configured: boolean;
  unauthorizedAccount: AdminAccount | null;
  onSignIn: () => void;
  onSignOut: () => void;
}) {
  return (
    <section className="panel auth-panel">
      <div className="panel-heading">
        <ShieldCheck size={20} aria-hidden="true" />
        <div>
          <h2>Access</h2>
          <p>{props.authReady ? 'Microsoft Entra ID' : 'Checking session'}</p>
        </div>
      </div>

      {props.authError ? <Notice tone="error" title="Sign-in failed" items={[props.authError]} /> : null}

      {props.unauthorizedAccount ? (
        <div className="signed-in-block">
          <div>
            <span>Signed in without admin access</span>
            <strong>{props.unauthorizedAccount.username}</strong>
          </div>
          <button type="button" onClick={props.onSignOut}>
            <LogOut size={17} aria-hidden="true" />
            <span>Use another account</span>
          </button>
        </div>
      ) : (
        <button className="primary-action" type="button" disabled={!props.authReady || !props.configured} onClick={props.onSignIn}>
          {props.authReady ? <LogIn size={18} aria-hidden="true" /> : <Loader2 className="spin" size={18} aria-hidden="true" />}
          <span>Sign in with Microsoft</span>
        </button>
      )}
    </section>
  );
}

function DevicesPanel(props: {
  devices: RegisteredPushDevice[];
  error: string;
  isFetching: boolean;
  onRefresh: () => void;
}) {
  return (
    <section className="panel devices-panel">
      <div className="panel-heading">
        <Smartphone size={20} aria-hidden="true" />
        <div>
          <h2>Token Registration</h2>
          <p>{props.devices.length} registered push token{props.devices.length === 1 ? '' : 's'}</p>
        </div>
      </div>

      <div className="section-toolbar">
        <div>
          <h3>Registered Devices</h3>
          <p>Safe token metadata only</p>
        </div>
        <button type="button" disabled={props.isFetching} onClick={props.onRefresh}>
          {props.isFetching ? <Loader2 className="spin" size={17} aria-hidden="true" /> : <Search size={17} aria-hidden="true" />}
          <span>Refresh</span>
        </button>
      </div>

      {props.error ? <Notice tone="error" title="Request failed" items={[props.error]} /> : null}

      <div className="device-list">
        {props.devices.length === 0 ? (
          <p className="empty-state">No registered push tokens.</p>
        ) : (
          props.devices.map((device) => (
            <article className="device-card" key={`${device.uid}-${device.token_prefix}`}>
              <div>
                <strong>{device.uid}</strong>
                <span>{device.platform} · {device.token_prefix}</span>
                <small>Last seen {formatDateTime(device.last_seen_at)}</small>
              </div>
              <div className={`status-badge status-${device.enabled ? 'sent' : 'error'}`}>
                {device.enabled ? 'enabled' : 'disabled'}
              </div>
              {device.disabled_at ? <p>Disabled {formatDateTime(device.disabled_at)}</p> : null}
              {device.last_error ? <p>{device.last_error}</p> : null}
            </article>
          ))
        )}
      </div>
    </section>
  );
}

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <label className="field">
      <span>{props.label}</span>
      {props.children}
    </label>
  );
}

function Stat(props: { label: string; value: string | number }) {
  return (
    <div className="stat-card">
      <span>{props.label}</span>
      <strong>{props.value}</strong>
    </div>
  );
}

function Notice(props: { tone: 'warning' | 'error'; title: string; items: string[] }) {
  return (
    <div className={`notice notice-${props.tone}`}>
      <CircleAlert size={18} aria-hidden="true" />
      <div>
        <strong>{props.title}</strong>
        {props.items.map((item) => <p key={item}>{item}</p>)}
      </div>
    </div>
  );
}

function updateForm<Key extends keyof DispatchForm>(
  key: Key,
  value: DispatchForm[Key],
  setForm: React.Dispatch<React.SetStateAction<DispatchForm>>,
) {
  setForm((current) => ({ ...current, [key]: value }));
}

function buildDispatchPayload(form: DispatchForm): NotificationDispatchRequest {
  const audience = buildAudience(form);
  const data = parseJsonObject(form.dataJson, 'Data JSON');
  const metadata = parseJsonObject(form.metadataJson, 'Metadata JSON');
  const ttlSeconds = form.ttlSeconds.trim() ? Number(form.ttlSeconds) : undefined;

  if (ttlSeconds !== undefined && (!Number.isInteger(ttlSeconds) || ttlSeconds < 0)) {
    throw new Error('TTL seconds must be a positive integer.');
  }

  return {
    intent: form.intent.trim(),
    audience,
    message: {
      title: form.title.trim(),
      body: form.body.trim(),
      data,
      ...(form.route.trim() ? { route: form.route.trim() } : {}),
      ...(form.symbol.trim() ? { symbol: form.symbol.trim().toUpperCase() } : {}),
    },
    delivery: {
      channels: ['push'],
      priority: form.priority,
      ...(form.sound.trim() ? { sound: form.sound.trim() } : {}),
      ...(ttlSeconds !== undefined ? { ttl_seconds: ttlSeconds } : {}),
    },
    metadata,
    dry_run: form.dryRun,
  };
}

function buildAudience(form: DispatchForm): NotificationAudience {
  if (form.audienceKind === 'uids' || form.audienceKind === 'test_devices') {
    const uids = form.uids
      .split(/[\n,]/)
      .map((uid) => uid.trim())
      .filter(Boolean);
    if (uids.length === 0) {
      throw new Error('UIDs are required for this audience.');
    }
    return { kind: form.audienceKind, uids };
  }

  if (form.audienceKind === 'platform') {
    return { kind: form.audienceKind, platform: form.platform };
  }

  return { kind: 'all_users' };
}

function parseJsonObject(text: string, label: string): Record<string, MetadataValue> {
  const trimmed = text.trim();
  if (!trimmed) {
    return {};
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed) as unknown;
  } catch (error) {
    throw new Error(`${label} is not valid JSON: ${error instanceof Error ? error.message : 'parse failed'}`);
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(`${label} must be a JSON object.`);
  }

  for (const [key, value] of Object.entries(parsed)) {
    if (!isMetadataValue(value)) {
      throw new Error(`${label}.${key} must be a string, number, boolean, or null. Nested objects and arrays are not accepted.`);
    }
  }

  return parsed as Record<string, MetadataValue>;
}

function isMetadataValue(value: unknown): value is MetadataValue {
  if (value == null || typeof value === 'string' || typeof value === 'boolean') {
    return true;
  }

  return typeof value === 'number' && Number.isFinite(value);
}

function describeAuthError(error: unknown): string {
  if (error instanceof ApiError && error.status === 403) {
    return 'This Microsoft account is not assigned the Ascensa Admin role.';
  }
  const message = error instanceof Error ? error.message : 'Sign-in failed.';
  if (message.includes('AADSTS50105')) {
    return 'This Microsoft account is not assigned to the Ascensa Admin application.';
  }
  return message;
}

function formatDateTime(value: string | null): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}
